import AndFilter from './filters/AndFilter';
import InFilter from './filters/InFilter';
import NotFilter from './filters/NotFilter';
import OrFilter from './filters/OrFilter';
import { ContainsFilter, StartsWithFilter, EndsWithFilter } from './filters/LikeFilter';

export const IN_TYPE = 'in';
export const LIKE_TYPE = 'like';
export const NOT_TYPE = 'not';
export const AND_TYPE = 'and';
export const OR_TYPE = 'or';

export const OPERATORS = {
  EQUALS: 'equals',
  DOES_NOT_EQUAL: 'does not equal',
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'does not contain',
  STARTS_WITH: 'starts with',
  DOES_NOT_START_WITH: 'does not start with',
  ENDS_WITH: 'ends with',
  DOES_NOT_END_WITH: 'does not end with',
};

export function getValueForLike(filter) {
  if (!filter.pattern) {
    return '';
  }
  if (filter.pattern.charAt(0) === '%') {
    if (filter.pattern.slice(-1) === '%') {
      return filter.pattern.slice(1, -1);
    } else {
      return filter.pattern.slice(1);
    }
  } else if (filter.pattern.slice(-1) === '%') {
    return filter.pattern.slice(0, -1);
  }
  return filter.pattern;
}

export function buildFilter(dimension, comparator, values) {
  const valuesToBuild = values && values[0] ? values : [''];
  switch (comparator) {
    case OPERATORS.EQUALS:
      return new InFilter(dimension, values);
    case OPERATORS.DOES_NOT_EQUAL:
      return new NotFilter(new InFilter(dimension, values));
    case OPERATORS.CONTAINS:
      return {
        ...new OrFilter(valuesToBuild.map((value) => new ContainsFilter(dimension, value))),
        comparator,
      };
    case OPERATORS.DOES_NOT_CONTAIN:
      return new NotFilter({
        ...new OrFilter(valuesToBuild.map((value) => new ContainsFilter(dimension, value))),
        comparator,
      });
    case OPERATORS.STARTS_WITH:
      return {
        ...new OrFilter(valuesToBuild.map((value) => new StartsWithFilter(dimension, value))),
        comparator,
      };
    case OPERATORS.DOES_NOT_START_WITH:
      return new NotFilter({
        ...new OrFilter(valuesToBuild.map((value) => new StartsWithFilter(dimension, value))),
        comparator,
      });
    case OPERATORS.ENDS_WITH:
      return {
        ...new OrFilter(valuesToBuild.map((value) => new EndsWithFilter(dimension, value))),
        comparator,
      };
    case OPERATORS.DOES_NOT_END_WITH:
      return new NotFilter({
        ...new OrFilter(valuesToBuild.map((value) => new EndsWithFilter(dimension, value))),
        comparator,
      });
  }
  return;
}

function _updateInFilter(filter, row, newDimension, comparator, values) {
  if (filter.dimension === row.dimension) {
    return buildFilter(newDimension || row.dimension, comparator || row.comparator, values || filter.values);
  }
  return filter;
}

function _updateLikeFilter(filter, row, newDimension, comparator, values) {
  if (filter.fields[0].dimension === row.dimension) {
    return buildFilter(
      newDimension || row.dimension,
      comparator || row.comparator,
      values || filter.fields.map((field) => getValueForLike(field))
    );
  }
  return filter;
}

function _updateNotFilter(filter, row, newDimension, comparator, values) {
  if (filter.field) {
    const { field } = filter;
    if (field.fields && field.fields[0] && field.fields[0].dimension === row.dimension) {
      return buildFilter(
        newDimension || row.dimension,
        comparator || row.comparator,
        values || field.fields.map((likeField) => getValueForLike(likeField))
      );
    } else if (field.dimension === row.dimension) {
      return buildFilter(newDimension || row.dimension, comparator || row.comparator, values || field.values);
    }
  }
  return filter;
}

function _updateFilter(filter, row, nestingLevel, newDimension, comparator, values) {
  switch (filter.type) {
    case IN_TYPE:
      return _updateInFilter(filter, row, newDimension, comparator, values);
    case LIKE_TYPE:
      console.error('Unsupported operation');
      return filter;
    case NOT_TYPE:
      return _updateNotFilter(filter, row, newDimension, comparator, values);
    case OR_TYPE:
      if (filter.fields[0] && filter.fields[0].type === LIKE_TYPE) {
        return _updateLikeFilter(filter, row, newDimension, comparator, values);
      }
      return new OrFilter(
        filter.fields
          .filter((field) => field)
          .map((field) => {
            return _updateFilter(field, row, nestingLevel - 1, newDimension, comparator, values);
          })
      );
    case AND_TYPE:
      return new AndFilter(
        filter.fields
          .filter((field) => field)
          .map((field) => {
            return _updateFilter(field, row, nestingLevel - 1, newDimension, comparator, values);
          })
      );
  }
}

export function updateFilter(filter, row, newDimension, comparator, values) {
  return _updateFilter(filter, row, row.nestingLevel, newDimension, comparator, values);
}

export function addFilter(filter, dimension, row, values) {
  if (!row.nestingLevel) {
    if (row.required) {
      return Object.assign({}, filter, {
        fields: filter.fields.concat(new AndFilter([new InFilter(dimension, values || [])])),
      });
    } else {
      return filter.type
        ? Object.assign({}, filter, {
            fields: filter.fields.concat(new InFilter(dimension, values || [])),
          })
        : new AndFilter([new InFilter(dimension, values || [])]);
    }
  }
  return addFilter(
    filter.fields.slice(-1)[0],
    dimension,
    Object.assign({}, row, {
      nestingLevel: row.nestingLevel - 1,
    })
  );
}

/**
 * Removes filter portions that are empty
 * inFilter -> not removed unless removeEmptyDimensions
 *   is true and values is empty
 * like filter -> removed only if removeEmptyDimensions is true
 *   and pattern is empty
 * NOTE: A filter with empty string as value is not removed unless
 *   removeEmptyDimensions is set to true
 * @param  {DruidFilter} filter
 * @param  {boolean} removeEmptyDimensions indicates whether to remove
 *   filters without value
 * @return {DruidFilter}
 */
export function cleanEmptyFilters(filter, removeEmptyDimensions) {
  if (!filter) {
    return;
  }
  switch (filter.type) {
    case AND_TYPE:
    case OR_TYPE:
      if (filter.fields.length) {
        const newFilter = Object.assign({}, filter, {
          fields: filter.fields.map((field) => cleanEmptyFilters(field, removeEmptyDimensions)).filter((field) => field),
        });
        return newFilter.fields.length ? newFilter : null;
      }
      return;
    case NOT_TYPE:
      const newNotFilter = cleanEmptyFilters(filter.field, removeEmptyDimensions);
      if (removeEmptyDimensions && !newNotFilter) {
        return;
      }
      return filter;
    case IN_TYPE:
      if (filter.values && !filter.values.length && removeEmptyDimensions) {
        return;
      }
      return filter;
    case LIKE_TYPE:
      if (!filter.pattern && removeEmptyDimensions) {
        return;
      }
      return filter;
    default:
      return filter;
  }
  return;
}

/**
 * Deletes a simple filter i.e., LIKE, IN, NOT
 * DO NOT USE THIS METHOD IF THE FILTER TYPE IS NOT ONE OF THE ABOVE
 * @param       {DruidFilter} filter
 * @param       {DisplayFormat} row
 * @return      {DruidFilter}
 */
function _deleteBasicFilter(filter, row) {
  switch (row.comparator) {
    case OPERATORS.EQUALS:
    case OPERATORS.CONTAINS:
    case OPERATORS.STARTS_WITH:
    case OPERATORS.ENDS_WITH:
      if (filter.dimension === row.dimension) {
        return;
      }
      return filter;
    case OPERATORS.DOES_NOT_EQUAL:
      if (filter.field && filter.field.dimension === row.dimension) {
        return;
      }
      return filter;
    case OPERATORS.DOES_NOT_CONTAIN:
    case OPERATORS.DOES_NOT_START_WITH:
    case OPERATORS.DOES_NOT_END_WITH:
      if (
        filter.field &&
        filter.field.fields &&
        filter.field.fields.length &&
        filter.field.fields[0].dimension === row.dimension
      ) {
        return;
      }
      return filter;
    default:
      return filter;
  }
}

/**
 * Internal method responsible for actually deleting the filter
 * Deleting the current filter will return undefined
 * Also checks if its a combination of filters and will remove the combo
 * if underlying filters are all empty
 * Example: And([]) => undefined
 * And([In, And([])]) => And([In])
 * @param       {DruidFilter} filter
 * @param       {DisplayFormat} row    Row corresponding to the filter to be deleted
 * @return      {DruidFilter}
 */
function _deleteFilter(filter, row) {
  if (filter.fields) {
    return Object.assign({}, filter, {
      fields: filter.fields.map((field) => _deleteFilter(field, row)).filter((field) => field),
    });
  }
  return _deleteBasicFilter(filter, row);
}

// may need to extend this method at some point to include a more comprehensive equality check
export const _isFilterEqual = (filter, dimension) => {
  return (
    (filter.dimension && filter.dimension === dimension) ||
    (filter.field && filter.field.dimension && filter.field.dimension === dimension) ||
    (filter.field &&
      filter.field.fields &&
      filter.field.fields.length &&
      filter.field.fields[0].dimension &&
      filter.field.fields[0].dimension === dimension)
  );
};

/**
 * Given row is deleted from the filter
 * DisplayFormat:
 * {
 *    dimension: key,
 *    comparator: 'equals',
 *    values: val,
 *    required,
 *    combiner
 * }
 * @param  {DruidFilter} filter
 * @param  {DisplayFormat} row
 * @return {DruidFilter}
 */
export function deleteFilter(filter, row) {
  if (row.required) {
    return filter;
  }

  if (_isFilterEqual(filter, row.dimension)) {
    return cleanEmptyFilters(_deleteFilter(filter, row));
  }

  if (!filter.fields) {
    return filter;
  }

  const fields = filter.fields.map((field) => deleteFilter(field, row)).filter((field) => field);
  if (fields.length) {
    return Object.assign({}, filter, {
      fields: fields,
    });
  }
  return;
}
